import axios from "axios";
import { Box, GitBranch, Globe, Settings } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createLogger } from "../../lib/logger";
import { useAppStore } from "../../store/useAppStore";
import { Deployment } from "../../types";
import { NewProjectDialog } from "../projects/NewProjectDialog";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { PageHeader } from "../ui/page-header";

const log = createLogger("Dashboard");

export function Dashboard() {
  const { projects, setProjects, setDeployments } = useAppStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [deploymentStats, setDeploymentStats] = useState({
    totalDeployments: 0,
    liveDeployments: 0,
    averageBuildTime: "0s",
    uptime: "0%",
  });

  const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/projects`);
      setProjects(response.data.projects);

      // Fetch all deployments for statistics
      const deploymentsResponse = await axios.get(`${API_BASE}/deployments`);
      const allDeployments: Deployment[] =
        deploymentsResponse.data.deployments || [];
      setDeployments(allDeployments);

      // Calculate deployment statistics
      const liveDeployments = allDeployments.filter(
        (d: Deployment) => d.status === "running"
      ).length;

      setDeploymentStats({
        totalDeployments: allDeployments.length,
        liveDeployments,
        averageBuildTime: allDeployments.length > 0 ? "2.4s" : "0s", // TODO: Calculate from real data
        uptime: liveDeployments > 0 ? "99.9%" : "0%", // TODO: Calculate from real data
      });
    } catch (error) {
      log.error("Failed to fetch projects", error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, setProjects, setDeployments, setDeploymentStats]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Workspace"
        title="Dashboard"
        description="Overview of your projects and deployments"
        actions={
        <div className="flex items-center gap-3">
          <NewProjectDialog
            open={showNewProjectDialog}
            onOpenChange={setShowNewProjectDialog}
            onProjectCreated={() => {
              setShowNewProjectDialog(false);
              fetchProjects();
            }}
          />
        </div>
        }
      />

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Projects
            </CardTitle>
            <GitBranch className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-h2">{projects.length}</div>
            <p className="text-xs text-muted-foreground">Active deployments</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Deployments</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-h2">
              {deploymentStats.totalDeployments}
            </div>
            <p className="text-xs text-muted-foreground">Total deployments</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Live Services</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-h2">
              {deploymentStats.liveDeployments}
            </div>
            <p className="text-xs text-muted-foreground">
              Running applications
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Build Time</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-h2">
              {deploymentStats.averageBuildTime}
            </div>
            <p className="text-xs text-muted-foreground">Average build time</p>
          </CardContent>
        </Card>
      </div>

      {/* Projects List */}
      <div>
        <div className="mb-4">
          <h3 className="text-h4">Your Projects</h3>
          <p className="text-sm text-muted-foreground">
            Recent project activity
          </p>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="text-muted-foreground">Loading projects...</div>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-12 bg-card border-dashed rounded-lg">
            <div className="text-muted-foreground">
              No projects yet. Create your first project from the Projects tab.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {projects.slice(0, 6).map((project) => (
              <div
                key={project.id}
                className="flex items-center justify-between p-4 bg-card rounded-lg hover:border-primary/50 cursor-pointer transition-colors"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Box className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-h5">{project.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {project.description || "No description"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-xs text-muted-foreground">
                    {new Date(project.createdAt).toLocaleDateString()}
                  </span>
                  <Button variant="ghost" size="sm">
                    View →
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
